import {
  APIDateOnlyFieldAttributes,
  APIDateTimeFieldAttributes,
  APIFieldAttributes,
  APIFieldType,
  APIGlobalField,
  ArdoqId,
  DateRangeFieldMapType,
  PlainDateISO,
  PlainDateTimeISO,
} from '@ardoq/api-types';
import { SortAttribute } from '@ardoq/common-helpers';
import { DateRangeFieldAttributes } from './types';
import {
  dashFormatDateRangeFieldLabel,
  extractDateRangeFieldLabel,
  fieldLabelIsPartOfDateRangeField,
} from './labelUtils';
import {
  extractDateRangeFieldName,
  fieldNameIsDateRangeEndField,
  fieldNameIsDateRangeStartField,
  fieldNameIsPartOfDateRangeField,
} from './nameUtils';
import { MAX_DATE_ISO_STRING, MIN_DATE_ISO_STRING } from './consts';
import { isDateFieldType } from '@ardoq/date-time';
import { uniq } from 'lodash';
import { DateRange } from '@ardoq/api-types';

/**
 * creates a date range field from the two date time fields.
 * APIGlobalField is a small subbset of APIFieldAttributes. So when fields of type APIGlobalField is used as input, the returned object will have much fewer attributes than when
 * using fields of type APIFieldAttributes as input. For instance APIGlobablField doesn't have _order, _id, or model
 */
export const createGlobalDateRangeField = <T extends APIGlobalField>(
  dateRangeType: APIFieldType,
  start: T,
  end: T
) => ({
  ...start,
  name: extractDateRangeFieldName(start.name),
  label: extractDateRangeFieldLabel(start.label),
  defaultValue: {
    start: start.defaultValue as string | null,
    end: end.defaultValue as string | null,
  },
  type: dateRangeType,
  // TODO ZhenyaUsenko: rename dateTimeFields (used both for dateTime and dateOnly range)
  dateTimeFields: {
    start,
    end,
  },
});

/**
 * creates a date range field from the two date time fields. The returned object will have the full set of attributes
 * that are expected for a field.
 */
export const createDateRangeField = (
  dateRangeType: APIFieldType,
  start: APIFieldAttributes,
  end: APIFieldAttributes
): DateRangeFieldAttributes => ({
  ...createGlobalDateRangeField(dateRangeType, start, end),
  [SortAttribute.ORDER]: start._order,
  componentType: uniq([...start.componentType, ...end.componentType]), // ARD-21466 this is a fix for a bug where sometimes these are not synchronised
  referenceType: uniq([...start.referenceType, ...end.referenceType]),
  _id: start._id,
});

export const isPartOfDateRangeField = ({
  label,
  name,
  type,
}: {
  label: string;
  name: string;
  type: APIFieldType;
}) =>
  isDateFieldType(type) &&
  fieldNameIsPartOfDateRangeField(name) &&
  fieldLabelIsPartOfDateRangeField(label);

export const fieldIsPartOfDateRangeField = (
  field: APIFieldAttributes
): field is APIDateTimeFieldAttributes | APIDateOnlyFieldAttributes => {
  return isPartOfDateRangeField(field);
};

/**
 * Check if two fields are part of the same date range field
 */
export const areMatchingDateTimeFields = (
  fieldA: APIFieldAttributes,
  fieldB: APIFieldAttributes
) => {
  if (fieldA.type !== fieldB.type || !isDateFieldType(fieldA.type)) {
    return false;
  }
  if (fieldA.model !== fieldB.model) {
    return false;
  }

  const isStartAndEndDates =
    (fieldNameIsDateRangeStartField(fieldA.name) &&
      fieldNameIsDateRangeEndField(fieldB.name)) ||
    (fieldNameIsDateRangeStartField(fieldB.name) &&
      fieldNameIsDateRangeEndField(fieldA.name));

  if (!isStartAndEndDates) {
    return false;
  }

  try {
    const hasSameBaseName =
      extractDateRangeFieldName(fieldA.name) ===
      extractDateRangeFieldName(fieldB.name);

    return hasSameBaseName;
  } catch (error) {
    return false;
  }
};

export const getDateRangeFieldsFromGlobalFields = (
  globalFields: APIGlobalField[]
) => {
  const potentialDateRanges = globalFields
    .filter(isPartOfDateRangeField)
    .reduce<Record<string, { start?: APIGlobalField; end?: APIGlobalField }>>(
      (acc, field) => {
        const dateRangeFieldName = extractDateRangeFieldName(field.name);
        const fieldsObject = fieldNameIsDateRangeEndField(field.name)
          ? { end: field }
          : { start: field };
        return acc[dateRangeFieldName]
          ? {
              ...acc,
              [dateRangeFieldName]: {
                ...acc[dateRangeFieldName],
                ...fieldsObject,
              },
            }
          : {
              ...acc,
              [dateRangeFieldName]: fieldsObject,
            };
      },
      {}
    );
  return Object.fromEntries(
    Object.entries(potentialDateRanges).filter(
      ([, { start, end }]) => start && end
    )
  ) as Record<string, { start: APIGlobalField; end: APIGlobalField }>;
};

export const getDateRangeFieldsGroupedByModelMap = (
  fields: APIFieldAttributes[]
): DateRangeFieldMapType => {
  // First group all potential date range fields by model and name
  const potentialDateRanges = fields
    .filter(isPartOfDateRangeField)
    .reduce<
      Record<
        ArdoqId,
        Record<string, { end?: APIFieldAttributes; start?: APIFieldAttributes }>
      >
    >((acc, field) => {
      const dateRangeFieldName = extractDateRangeFieldName(field.name);
      const fieldsObject = fieldNameIsDateRangeEndField(field.name)
        ? { end: field }
        : { start: field };
      const modelId = field.model;
      if (acc[modelId]?.[dateRangeFieldName]) {
        acc[modelId][dateRangeFieldName] = {
          ...acc[modelId][dateRangeFieldName],
          ...fieldsObject,
        };
      } else if (acc[modelId]) {
        acc[modelId][dateRangeFieldName] = fieldsObject;
      } else {
        acc[modelId] = { [dateRangeFieldName]: fieldsObject };
      }
      return acc;
    }, {});

  // Then filter out all date range fields that don't have both start and end fields
  const dateRangesForWorkspaces = Object.entries(potentialDateRanges).reduce(
    (acc, [modelId, fields]) => {
      const completeFields = Object.entries(fields).reduce(
        (fieldsWithStartAndEnd, [fieldName, { start, end }]) =>
          start && end
            ? { ...fieldsWithStartAndEnd, [fieldName]: { start, end } }
            : fieldsWithStartAndEnd,
        {}
      );
      return Object.keys(completeFields).length
        ? {
            ...acc,
            [modelId]: completeFields,
          }
        : acc;
    },
    {}
  );
  return new Map(Object.entries(dateRangesForWorkspaces));
};

export const getDateRangeFieldsGroupedByModelAndWorkspace = (
  fields: APIFieldAttributes[],
  modelIdWorkspaceIdMap: Map<ArdoqId, ArdoqId>
): DateRangeFieldMapType =>
  new Map(
    Array.from(getDateRangeFieldsGroupedByModelMap(fields)).flatMap(
      ([modelId, dateRangeFields]) => {
        const workspaceId = modelIdWorkspaceIdMap.get(modelId);
        if (workspaceId) {
          return [
            [modelId, dateRangeFields],
            [workspaceId, dateRangeFields],
          ];
        }
        return [[modelId, dateRangeFields]];
      }
    )
  );

export const getFieldIdAndLabel = (
  field: APIFieldAttributes | DateRangeFieldAttributes
) => {
  if (isDateRangeField(field)) {
    const { start, end } = field.dateTimeFields;
    return [
      {
        id: start._id,
        label: dashFormatDateRangeFieldLabel(start.label),
      },
      {
        id: end._id,
        label: dashFormatDateRangeFieldLabel(end.label),
      },
    ];
  }
  return {
    id: field._id,
    label: field.label,
  };
};

export const getDateRangeType = (fieldType: APIFieldType) =>
  fieldType === APIFieldType.DATE_ONLY
    ? APIFieldType.DATE_ONLY_RANGE
    : APIFieldType.DATE_TIME_RANGE;

export const getDateRangePartType = (fieldType: APIFieldType) =>
  fieldType === APIFieldType.DATE_ONLY_RANGE
    ? APIFieldType.DATE_ONLY
    : APIFieldType.DATE_TIME;

export const isDateRangeFieldType = (
  fieldType: unknown
): fieldType is APIFieldType.DATE_ONLY_RANGE | APIFieldType.DATE_TIME_RANGE =>
  fieldType === APIFieldType.DATE_ONLY_RANGE ||
  fieldType === APIFieldType.DATE_TIME_RANGE;

export const isDateRangeField = (
  field: unknown
): field is DateRangeFieldAttributes => {
  if (!field || typeof field !== 'object') return false;

  const hasType = 'type' in field;
  const hasDateTimeFields = 'dateTimeFields' in field;
  if (!hasType || !hasDateTimeFields) return false;

  return isDateRangeFieldType(field.type);
};

export const toCombinedDateRangeFieldName = (
  dateRangeField: DateRangeFieldAttributes
) =>
  `${dateRangeField.dateTimeFields.start.name},${dateRangeField.dateTimeFields.end.name}`;

export const toCombinedDateRangeFieldIds = (
  dateRangeField: DateRangeFieldAttributes
) =>
  `${dateRangeField.dateTimeFields.start._id},${dateRangeField.dateTimeFields.end._id}`;

export const isComponentOfDateRangeField = ({
  field,
  dateRangeField,
}: {
  field: APIFieldAttributes;
  dateRangeField: DateRangeFieldAttributes;
}) =>
  (dateRangeField.dateTimeFields.start.name === field.name ||
    dateRangeField.dateTimeFields.end.name === field.name) &&
  dateRangeField.model === field.model;

type NullableDateRange = { start: string | null; end: string | null };

/**
 * Replaces null values in a date range with MIN_DATE or MAX_DATE.
 * If both values are null, returns them unchanged.
 * If start is null but end is defined, start becomes MIN_DATE.
 * If end is null but start is defined, end becomes MAX_DATE.
 *
 * @param dateRange - An object with start and end dates where either or both can be null
 * @returns A DateRange with null values replaced by MIN_DATE_ISO_STRING or MAX_DATE_ISO_STRING
 */
export const replaceEmptyDates = ({
  start,
  end,
}: NullableDateRange): DateRange =>
  start === null && end === null
    ? { start, end }
    : { start: start ?? MIN_DATE_ISO_STRING, end: end ?? MAX_DATE_ISO_STRING };

export type ReplaceEmptyDateRangePartFieldValueArgs = {
  fieldName: string;
  fieldType: APIFieldType;
  currentFieldValue: PlainDateISO | PlainDateTimeISO | null;
  dateRangeFieldCounterpartValue: PlainDateISO | PlainDateTimeISO | null;
};
/**
 * Special handling for date ranges with only one date defined: if date field is part of a date range and its
 * value === null, the date range field pair represents an infinite time span, starting or ending on defined
 * date. For convenience of not dealing with Infinity values, we use MIN_DATE and MAX_DATE.
 */
export const replaceEmptyDateRangePartFieldValue = ({
  fieldName,
  fieldType,
  currentFieldValue,
  dateRangeFieldCounterpartValue,
}: ReplaceEmptyDateRangePartFieldValueArgs) => {
  if (
    !fieldNameIsPartOfDateRangeField(fieldName) ||
    !isDateFieldType(fieldType) ||
    currentFieldValue !== null
  ) {
    return currentFieldValue;
  }

  const isStartField = fieldNameIsDateRangeStartField(fieldName);
  const dateRange: DateRange = isStartField
    ? { start: currentFieldValue, end: dateRangeFieldCounterpartValue }
    : { start: dateRangeFieldCounterpartValue, end: currentFieldValue };
  const { start: replacedStart, end: replacedEnd } =
    replaceEmptyDates(dateRange);

  return isStartField ? replacedStart : replacedEnd;
};

/**
 * Type guard to check if a value is a DateRange structure with start/end properties.
 */
export const isDateRangeValue = (value: unknown): value is DateRange => {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('start' in value || 'end' in value)
  );
};
